import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { DxTradeRawCandle } from '@model/DxTrade.types'

export type DxtradeLatestCandlesBySymbol = Record<string, DxTradeRawCandle | undefined>

export const useDxtradeLatestCandles = (
  candleTypeId: string,
  symbols?: string[]
): {
  candlesBySymbol: DxtradeLatestCandlesBySymbol
  isLoading: boolean
} => {
  const apiClient = useApiClient(ClientApiClient)
  const [candlesBySymbol, setCandlesBySymbol] = useState<DxtradeLatestCandlesBySymbol>({})
  const [isLoading, setIsLoading] = useState(false)
  const fetchedRef = useRef<Set<string>>(new Set())

  const uniqueSymbols = useMemo(() => {
    if (!symbols?.length || !candleTypeId) return []
    return Array.from(new Set(symbols))
  }, [symbols, candleTypeId])

  const newSymbols = useMemo(
    () => uniqueSymbols.filter((s) => !fetchedRef.current.has(s)),
    [uniqueSymbols]
  )

  const fetchCandles = useCallback(
    async (symbolsToFetch: string[]) => {
      if (!symbolsToFetch.length) return

      symbolsToFetch.forEach((s) => fetchedRef.current.add(s))
      setIsLoading(true)

      try {
        const candles = await apiClient.getDxtradeLatestCandles(symbolsToFetch, candleTypeId)

        const parsed = Object.fromEntries(
          candles.map((c) => [c.symbol, c])
        ) as DxtradeLatestCandlesBySymbol

        setCandlesBySymbol((prev) => ({ ...prev, ...parsed }))
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [apiClient, candleTypeId]
  )

  useEffect(() => {
    if (newSymbols.length > 0) {
      fetchCandles(newSymbols)
    }
  }, [newSymbols, fetchCandles])

  return {
    candlesBySymbol,
    isLoading,
  }
}
